import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { useApi } from '../api/client';
import { useAuth } from '../auth/AuthProvider';
import PeriodToolbar from '../components/PeriodToolbar';
import { usePeriod } from '../context/PeriodContext';
import { SkeletonCard } from '../components/Skeleton';
import { ErrorState } from '../components/ErrorState';
import type { ApiError } from '../api/client';

interface SummarySelf {
  totalAssignments:number;
  selfRated:number;
  managerRated:number;
  pendingSelf:number;
  pendingManager:number;
  averageSelfScore?: number | null;
  averageManagerScore?: number | null;
  weightTotal?: number;
  weightedContribution?: number;
}

interface SummaryTeam {
  totalTeamAssignments:number;
  employeesWithAllSelfDone:number;
  employeesWithAllManagerDone:number;
  outstandingManagerReviews:number;
  outstandingSelfRatings?:number;
  weightTotal?: number;
  weightedContribution?: number;
}

interface DashboardSummary { self: SummarySelf; team?: SummaryTeam; }

interface IncentiveData {
  employeeName: string;
  employeeUpn: string;
  annualTarget: number;
  actualsBilled: number;
  deficit: number;
  annualBonusGross?: number;
  annualBonusTax?: number | null;
  annualBonusNet?: number | null;
  annualBonusEarned?: boolean;
  annualBonusSource?: string;
  quarterlySales: Array<{
    quarter: string;
    tracker: number;
    amount: number;
    forecast?: number;
    bonusEarned?: boolean;
    bonusAmount?: number;
  }>;
  potentialIncentives: {
    quarterlyBonus: number;
    annualBonus: number;
    kpiBonus: number;
    total: number;
  };
  kpiScore?: {
    avgScoreRaw: number | null;
    avgScorePct: number | null;
    eligible: boolean;
    periodsWithData: number;
    totalPeriods: number;
  };
}

export const DashboardPage: React.FC = () => {
  const { user, profileLoading } = useAuth();
  const api = useApi();
  const { dueDate } = usePeriod();
  const [showAll, setShowAll] = React.useState(true);
  const [selectedEmployee, setSelectedEmployee] = React.useState<string | null>(null);
  
  const q = useQuery({
    queryKey:['dashboard-summary', showAll ? 'all' : dueDate, user?.upn],
    enabled: !!user?.upn && !profileLoading,
    retry: 2,
    staleTime: 30000,
    refetchOnWindowFocus: true,
    queryFn: async ():Promise<DashboardSummary> => {
      const endDateParam = showAll ? '2099-12-31' : dueDate;
      const data = await api.get(`/dashboard/summary?endDate=${endDateParam}`);
      return data as DashboardSummary;
    }
  });

  const incentiveQ = useQuery({
    queryKey:['incentive-data', selectedEmployee || user?.upn],
    enabled: !!user?.upn && !profileLoading,
    retry: 2,
    staleTime: 30000,
    queryFn: async ():Promise<IncentiveData> => {
      const upn = selectedEmployee || user?.upn;
      const data = await api.get(`/dashboard/incentives?upn=${encodeURIComponent(upn)}`);
      return data as IncentiveData;
    }
  });

  const teamMembersQ = useQuery({
    queryKey:['team-members', user?.upn],
    enabled: !!user?.upn && !profileLoading && q.data?.team !== undefined,
    retry: 2,
    staleTime: 60000,
    queryFn: async () => {
      const data = await api.get('/employees/directs');
      return data as Array<{ upn: string; displayName: string }>;
    }
  });

  interface TeamIncentiveRow {
    employeeUpn: string;
    displayName: string;
    annualTarget: number;
    actualsBilled: number;
    annualTracker: number;
    quarters: Array<{ quarter: string; tracker: number; bonusEarned: boolean; bonusAmount: number }>;
    quarterlyBonusTotal: number;
    annualBonus: number;
    annualBonusGross?: number;
    annualBonusTax?: number | null;
    annualBonusNet?: number | null;
    annualBonusEarned?: boolean;
    kpiBonus: number;
    kpiScore?: { avgScorePct: number | null; eligible: boolean; periodsWithData: number; totalPeriods: number };
    totalIncentive: number;
  }

  const teamIncentivesQ = useQuery({
    queryKey: ['team-incentives', user?.upn],
    enabled: !!user?.upn && !profileLoading && q.data?.team !== undefined,
    retry: 2,
    staleTime: 30000,
    queryFn: async (): Promise<TeamIncentiveRow[]> => {
      const data = await api.get('/dashboard/team-incentives');
      return data as TeamIncentiveRow[];
    }
  });

  const pct = (num:number, den:number) => den === 0 ? 0 : Math.round((num/den)*100);
  const fmt = (value?: number | null, digits = 2) => value === undefined || value === null ? '-' : value.toFixed(digits);

  if (profileLoading || q.isLoading) {
    return (
      <div className="logo-loader-wrap">
        <img src="/logo.jpeg" alt="Loading" />
        <span className="loader-text">Loading dashboard...</span>
      </div>
    );
  }

  if (q.error || !q.data) {
    return (
      <div>
        <h2>Performance Snapshot</h2>
        <ErrorState
          title="Failed to Load Dashboard"
          message={(q.error as ApiError)?.body?.detail || 'Unable to load your dashboard data. Please try again.'}
          onRetry={() => q.refetch()}
        />
      </div>
    );
  }

  const { self, team } = q.data;

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: '0.5rem' }}>
        <h2 style={{ margin: 0 }}>Performance Snapshot</h2>
        <button
          className={`refresh-btn${q.isFetching ? ' spinning' : ''}`}
          onClick={() => q.refetch()}
          title="Refresh data"
          disabled={q.isFetching}
        >↻</button>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1rem' }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
          <input 
            type="checkbox" 
            checked={showAll} 
            onChange={(e) => setShowAll(e.target.checked)} 
          />
          Show all periods
        </label>
        {!showAll && <PeriodToolbar />}
      </div>
      <section className="card-grid">
        <Card title="KPIs" value={self.totalAssignments} subtitle="current period" />
        <Card title="Self Rated" value={`${self.selfRated}/${self.totalAssignments}`} subtitle={`${pct(self.selfRated,self.totalAssignments)}% complete`} />
        <Card title="Manager Rated" value={`${self.managerRated}/${self.totalAssignments}`} subtitle={`${pct(self.managerRated,self.totalAssignments)}% reviewed`} />
        <Card title="Avg Self Score" value={fmt(self.averageSelfScore)} subtitle="/5" />
        <Card title="Avg Manager Score" value={fmt(self.averageManagerScore)} subtitle="/5" />
        <Card title="Weight Coverage" value={fmt(self.weightTotal, 3)} subtitle="sum of KPI weights" />
        <Card title="Weighted Contribution" value={fmt(self.weightedContribution, 3)} subtitle="impact so far" />
        <Card title="Pending Self" value={self.pendingSelf} subtitle="needs your input" />
        <Card title="Pending Manager" value={self.pendingManager} subtitle="awaiting manager" />
      </section>

      {team && (
        <section style={{marginTop:'2rem'}}>
          <h3>Team Overview</h3>
          <div className="card-grid">
            <Card title="Assignments" value={team.totalTeamAssignments} subtitle="across directs" />
            <Card title="Employees self-complete" value={team.employeesWithAllSelfDone} subtitle="all KPIs rated" />
            <Card title="Employees manager-complete" value={team.employeesWithAllManagerDone} subtitle="all reviews done" />
            <Card title="Outstanding Manager Reviews" value={team.outstandingManagerReviews} subtitle="self done, waiting on you" />
            <Card title="Outstanding Self Ratings" value={team.outstandingSelfRatings ?? 0} subtitle="employees yet to self-rate" />
            <Card title="Weight Coverage" value={fmt(team.weightTotal, 3)} subtitle="sum of weights" />
            <Card title="Team Contribution" value={fmt(team.weightedContribution, 3)} subtitle="aggregate impact" />
          </div>
        </section>
      )}

      {/* Incentive Dashboard Section */}
      <section style={{marginTop:'2rem', borderTop: '2px solid #e5e7eb', paddingTop: '2rem'}}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <h3 style={{ margin: 0 }}>Incentive Dashboard</h3>
          {team && (
            <select 
              value={selectedEmployee || user?.upn || ''}
              onChange={(e) => setSelectedEmployee(e.target.value === user?.upn ? null : e.target.value)}
              style={{ padding: '6px 12px', fontSize: '14px', borderRadius: '4px', border: '1px solid #d1d5db' }}
            >
              <option value={user?.upn || ''}>{user?.displayName || 'My Incentives'}</option>
              {teamMembersQ.data && teamMembersQ.data.length > 0 && (
                <>
                  <option disabled>─── Team Members ───</option>
                  {teamMembersQ.data.map(emp => (
                    <option key={emp.upn} value={emp.upn}>{emp.displayName}</option>
                  ))}
                </>
              )}
            </select>
          )}
        </div>
        
        {incentiveQ.isLoading ? (
          <div style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>
            Loading incentive data...
          </div>
        ) : incentiveQ.error ? (
          <div style={{ textAlign: 'center', padding: '2rem', color: '#ef4444' }}>
            No incentive data available yet. Manual entry coming soon.
          </div>
        ) : incentiveQ.data && (
          <IncentiveDashboard data={incentiveQ.data} />
        )}
      </section>

      {/* Team Incentive Summary Table (managers only) */}
      {team && teamIncentivesQ.data && teamIncentivesQ.data.length > 0 && (
        <section style={{ marginTop: '2rem', borderTop: '2px solid #e5e7eb', paddingTop: '2rem' }}>
          <h3 style={{ margin: '0 0 1rem' }}>Team Incentive Summary</h3>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', fontSize: '13px', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #e5e7eb', background: '#f9fafb' }}>
                  <th style={{ textAlign: 'left', padding: '0.5rem 0.75rem', fontWeight: 600 }}>Employee</th>
                  <th style={{ textAlign: 'right', padding: '0.5rem 0.75rem', fontWeight: 600 }}>Target</th>
                  <th style={{ textAlign: 'right', padding: '0.5rem 0.75rem', fontWeight: 600 }}>Billed</th>
                  <th style={{ textAlign: 'center', padding: '0.5rem 0.75rem', fontWeight: 600 }}>Q1</th>
                  <th style={{ textAlign: 'center', padding: '0.5rem 0.75rem', fontWeight: 600 }}>Q2</th>
                  <th style={{ textAlign: 'center', padding: '0.5rem 0.75rem', fontWeight: 600 }}>Q3</th>
                  <th style={{ textAlign: 'center', padding: '0.5rem 0.75rem', fontWeight: 600 }}>Q4</th>
                  <th style={{ textAlign: 'center', padding: '0.5rem 0.75rem', fontWeight: 600 }}>KPI %</th>
                  <th style={{ textAlign: 'right', padding: '0.5rem 0.75rem', fontWeight: 600 }}>Annual Bonus</th>
                  <th style={{ textAlign: 'right', padding: '0.5rem 0.75rem', fontWeight: 600 }}>Net Pay</th>
                  <th style={{ textAlign: 'right', padding: '0.5rem 0.75rem', fontWeight: 600 }}>Total Incentive</th>
                </tr>
              </thead>
              <tbody>
                {teamIncentivesQ.data.map((row) => {
                  const cf = (v: number | null | undefined) =>
                    v === null || v === undefined
                      ? '-'
                      : `R ${v.toLocaleString('en-ZA', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
                  const annualBonusGross = row.annualBonusGross ?? row.annualBonus;
                  return (
                    <tr key={row.employeeUpn} style={{ borderBottom: '1px solid #e5e7eb' }}>
                      <td style={{ padding: '0.5rem 0.75rem', fontWeight: 500 }}>{row.displayName}</td>
                      <td style={{ textAlign: 'right', padding: '0.5rem 0.75rem' }}>{cf(row.annualTarget)}</td>
                      <td style={{ textAlign: 'right', padding: '0.5rem 0.75rem' }}>{cf(row.actualsBilled)}</td>
                      {row.quarters.map((q, qi) => (
                        <td key={qi} style={{ textAlign: 'center', padding: '0.5rem 0.75rem' }}>
                          {q.tracker > 0 ? (
                            <span style={{ color: q.bonusEarned ? '#10b981' : '#ef4444', fontWeight: 600 }}>
                              {q.tracker.toFixed(0)}%
                            </span>
                          ) : (
                            <span style={{ color: '#d1d5db' }}>-</span>
                          )}
                        </td>
                      ))}
                      <td style={{ textAlign: 'center', padding: '0.5rem 0.75rem' }}>
                        {row.kpiScore?.avgScorePct != null ? (
                          <span style={{ color: row.kpiScore.eligible ? '#10b981' : '#ef4444', fontWeight: 600 }}>
                            {row.kpiScore.avgScorePct.toFixed(0)}%
                          </span>
                        ) : (
                          <span style={{ color: '#d1d5db' }}>-</span>
                        )}
                      </td>
                      <td style={{ textAlign: 'right', padding: '0.5rem 0.75rem', fontWeight: 600 }}>
                        {cf(annualBonusGross)}
                      </td>
                      <td style={{ textAlign: 'right', padding: '0.5rem 0.75rem', fontWeight: 600 }}>
                        {cf(row.annualBonusNet ?? null)}
                      </td>
                      <td style={{ textAlign: 'right', padding: '0.5rem 0.75rem', fontWeight: 600 }}>
                        {cf(row.totalIncentive)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </div>
  );
};

const Card:React.FC<{title:string; value:React.ReactNode; subtitle?:string}> = ({title,value,subtitle}) => (
  <div className="summary-card">
    <div className="summary-card-title">{title}</div>
    <div className="summary-card-value">{value}</div>
    {subtitle && <div className="summary-card-subtitle">{subtitle}</div>}
  </div>
);

const IncentiveDashboard: React.FC<{ data: IncentiveData }> = ({ data }) => {
  const currencyFormat = (val: number) => `R ${val.toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const moneyOrDash = (val?: number | null) =>
    val === undefined || val === null ? '-' : currencyFormat(val);
  const deficitColor = data.deficit < 0 ? '#dc2626' : '#059669';

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2rem' }}>
      {/* Left side - Sales Summary */}
      <div>
        <div style={{ background: '#f9fafb', padding: '1.5rem', borderRadius: '8px', marginBottom: '1.5rem' }}>
          <table style={{ width: '100%', fontSize: '14px' }}>
            <tbody>
              <tr>
                <td style={{ padding: '0.5rem 0', fontWeight: 600 }}>Annual Sales Target</td>
                <td style={{ textAlign: 'right', fontWeight: 600 }}>{currencyFormat(data.annualTarget)}</td>
              </tr>
              <tr>
                <td style={{ padding: '0.5rem 0' }}>Actuals billed</td>
                <td style={{ textAlign: 'right' }}>{currencyFormat(data.actualsBilled)}</td>
              </tr>
              <tr style={{ borderTop: '2px solid #e5e7eb' }}>
                <td style={{ padding: '0.5rem 0', fontWeight: 600, color: deficitColor }}>
                  {data.deficit < 0 ? 'Deficit' : 'Surplus'}
                </td>
                <td style={{ textAlign: 'right', fontWeight: 600, color: deficitColor }}>
                  {currencyFormat(Math.abs(data.deficit))}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Quarterly Tracker Gauges */}
        <div style={{ background: '#f9fafb', padding: '1.5rem', borderRadius: '8px' }}>
          <div style={{ fontSize: '13px', fontWeight: 600, color: '#374151', marginBottom: '1rem' }}>Quarterly Performance</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '1rem' }}>
            {data.quarterlySales.map((q, idx) => {
              const hasData = (q.forecast ?? 0) > 0;
              const tracker = q.tracker;
              const ringColor = !hasData ? '#d1d5db' : tracker >= 100 ? '#10b981' : tracker >= 75 ? '#f59e0b' : '#ef4444';
              const r = 34;
              const circ = 2 * Math.PI * r;
              const pctClamped = hasData ? Math.min(tracker / 100, 1.4) : 0;
              const offset = circ - pctClamped * circ;

              return (
                <div key={idx} style={{
                  textAlign: 'center',
                  padding: '1rem',
                  background: '#ffffff',
                  borderRadius: '8px',
                  border: '1px solid #e5e7eb',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minHeight: '160px',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                }}>
                  <div style={{ position: 'relative', display: 'inline-block', marginBottom: '0.25rem' }}>
                    <svg width="84" height="84" viewBox="0 0 84 84">
                      <circle cx="42" cy="42" r={r} fill="none" stroke="#e5e7eb" strokeWidth="7" />
                      {hasData && (
                        <circle
                          cx="42" cy="42" r={r}
                          fill="none"
                          stroke={ringColor}
                          strokeWidth="7"
                          strokeLinecap="round"
                          strokeDasharray={circ}
                          strokeDashoffset={offset}
                          transform="rotate(-90 42 42)"
                          style={{ transition: 'stroke-dashoffset 0.8s ease-out' }}
                        />
                      )}
                    </svg>
                    <div style={{
                      position: 'absolute', top: '50%', left: '50%',
                      transform: 'translate(-50%, -50%)',
                      fontSize: hasData ? '15px' : '14px', fontWeight: 700,
                      color: hasData ? ringColor : '#9ca3af',
                    }}>
                      {hasData ? `${tracker.toFixed(0)}%` : '--'}
                    </div>
                  </div>

                  <div style={{ fontSize: '13px', fontWeight: 600, color: '#374151' }}>{q.quarter}</div>

                  {hasData && (
                    <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '2px' }}>
                      {currencyFormat(q.amount)} / {currencyFormat(q.forecast!)}
                    </div>
                  )}

                  <div style={{ marginTop: '0.35rem' }}>
                    {q.bonusEarned ? (
                      <span style={{
                        display: 'inline-block', padding: '2px 8px', borderRadius: '10px',
                        fontSize: '10px', fontWeight: 700,
                        background: '#d1fae5', color: '#065f46',
                        boxShadow: '0 0 6px rgba(16,185,129,0.3)',
                      }}>
                        R 5,000
                      </span>
                    ) : hasData ? (
                      <span style={{
                        display: 'inline-block', padding: '2px 8px', borderRadius: '10px',
                        fontSize: '10px', fontWeight: 600,
                        background: '#f3f4f6', color: '#9ca3af',
                      }}>
                        R 0
                      </span>
                    ) : null}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Annual Progress Bar */}
          {(() => {
            const annualPct = data.annualTarget > 0
              ? Math.round((data.actualsBilled / data.annualTarget) * 1000) / 10
              : 0;
            const barColor = annualPct >= 100 ? '#10b981' : annualPct >= 75 ? '#f59e0b' : '#ef4444';
            return (
              <div style={{ marginTop: '1.25rem', paddingTop: '1rem', borderTop: '1px solid #e5e7eb' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '6px' }}>
                  <span style={{ fontSize: '12px', fontWeight: 600, color: '#374151' }}>Annual Tracker</span>
                  <span style={{ fontSize: '14px', fontWeight: 700, color: barColor }}>{annualPct.toFixed(1)}%</span>
                </div>
                <div style={{ height: '10px', background: '#e5e7eb', borderRadius: '5px', overflow: 'hidden' }}>
                  <div style={{
                    height: '100%',
                    width: `${Math.min(annualPct, 100)}%`,
                    background: `linear-gradient(90deg, ${barColor}cc, ${barColor})`,
                    borderRadius: '5px',
                    transition: 'width 0.8s ease-out',
                  }} />
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', color: '#9ca3af', marginTop: '4px' }}>
                  <span>Billed: {currencyFormat(data.actualsBilled)}</span>
                  <span>Target: {currencyFormat(data.annualTarget)}</span>
                </div>
              </div>
            );
          })()}
        </div>
      </div>

      {/* Right side - Incentives */}
      <div>
        <h4 style={{ marginTop: 0, marginBottom: '1rem', color: '#374151' }}>Potential Incentives</h4>
        <table style={{ width: '100%', fontSize: '14px', marginBottom: '1.5rem' }}>
          <thead>
            <tr style={{ borderBottom: '2px solid #e5e7eb' }}>
              <th style={{ textAlign: 'left', padding: '0.5rem 0', fontWeight: 600 }}>Description</th>
              <th style={{ textAlign: 'center', padding: '0.5rem 0', fontWeight: 600 }}>Tracker</th>
              <th style={{ textAlign: 'right', padding: '0.5rem 0', fontWeight: 600 }}>Amount</th>
            </tr>
          </thead>
          <tbody>
            {(() => {
              const quartersEarned = data.quarterlySales.filter(q => q.bonusEarned).length;
              const anyQuarterEarned = quartersEarned > 0;
              const annualTarget = data.annualTarget;
              const annualMet = annualTarget > 0 && data.actualsBilled >= annualTarget;
              const annualEarned = data.annualBonusEarned ?? annualMet;
              return (
                <>
                  <tr>
                    <td style={{ padding: '0.5rem 0' }}>
                      Quarterly sales incentive
                      <span style={{ marginLeft: '0.5rem', fontSize: '12px', color: '#6b7280' }}>
                        - {quartersEarned}/4 quarters met
                      </span>
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      {anyQuarterEarned
                        ? <span style={{ color: '#10b981' }}>✓</span>
                        : <span style={{ color: '#ef4444' }}>✗</span>}
                    </td>
                    <td style={{ textAlign: 'right' }}>{currencyFormat(data.potentialIncentives.quarterlyBonus)}</td>
                  </tr>
                  <tr>
                    <td style={{ padding: '0.5rem 0' }}>
                      Annual sales incentive
                      {annualTarget > 0 && (
                        <span style={{ marginLeft: '0.5rem', fontSize: '12px', color: '#6b7280' }}>
                          - {((data.actualsBilled / annualTarget) * 100).toFixed(1)}% of target
                        </span>
                      )}
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      {annualEarned
                        ? <span style={{ color: '#10b981' }}>✓</span>
                        : <span style={{ color: '#ef4444' }}>✗</span>}
                    </td>
                    <td style={{ textAlign: 'right' }}>{currencyFormat(data.potentialIncentives.annualBonus)}</td>
                  </tr>
                </>
              );
            })()}
            <tr style={{ borderTop: '1px solid #e5e7eb' }}>
              <td style={{ padding: '0.5rem 0' }}>
                KPI avg ≥ 70% (Dec–Nov)
                {data.kpiScore && data.kpiScore.avgScorePct !== null && (
                  <span style={{ marginLeft: '0.5rem', fontSize: '12px', color: '#6b7280' }}>
                    - current avg: {data.kpiScore.avgScorePct.toFixed(1)}%
                    ({data.kpiScore.periodsWithData}/{data.kpiScore.totalPeriods} months rated)
                  </span>
                )}
              </td>
              <td style={{ textAlign: 'center' }}>
                {data.kpiScore ? (
                  data.kpiScore.avgScorePct === null ? (
                    <span style={{ color: '#9ca3af', fontSize: '12px' }}>-</span>
                  ) : data.kpiScore.eligible ? (
                    <span style={{ color: '#10b981' }}>✓</span>
                  ) : (
                    <span style={{ color: '#ef4444' }}>✗</span>
                  )
                ) : (
                  <span style={{ color: '#9ca3af', fontSize: '12px' }}>-</span>
                )}
              </td>
              <td style={{ textAlign: 'right' }}>{currencyFormat(data.potentialIncentives.kpiBonus)}</td>
            </tr>
            <tr style={{ borderTop: '2px solid #e5e7eb', fontWeight: 600 }}>
              <td style={{ padding: '0.5rem 0' }}>Total Potential</td>
              <td></td>
              <td style={{ textAlign: 'right' }}>{currencyFormat(data.potentialIncentives.total)}</td>
            </tr>
          </tbody>
        </table>

        <div style={{ background: '#f9fafb', padding: '1rem 1.25rem', borderRadius: '8px', border: '1px solid #e5e7eb', marginBottom: '1.5rem' }}>
          <div style={{ fontSize: '12px', fontWeight: 700, color: '#374151', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
            Annual Bonus Take-Home
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1rem', marginTop: '0.75rem', fontSize: '13px' }}>
            <div>
              <div style={{ color: '#6b7280', marginBottom: '0.25rem' }}>Gross</div>
              <div style={{ fontWeight: 700 }}>{moneyOrDash(data.annualBonusGross ?? data.potentialIncentives.annualBonus)}</div>
            </div>
            <div>
              <div style={{ color: '#6b7280', marginBottom: '0.25rem' }}>SARS Tax</div>
              <div style={{ fontWeight: 700 }}>{moneyOrDash(data.annualBonusTax)}</div>
            </div>
            <div>
              <div style={{ color: '#6b7280', marginBottom: '0.25rem' }}>Net Pay</div>
              <div style={{ fontWeight: 700 }}>{moneyOrDash(data.annualBonusNet)}</div>
            </div>
          </div>
          {data.annualBonusTax == null && (
            <div style={{ marginTop: '0.5rem', fontSize: '11px', color: '#9ca3af' }}>
              Tax table not configured yet.
            </div>
          )}
        </div>

        <h4 style={{ marginBottom: '1rem', color: '#374151' }}>Actuals (year to date)</h4>
        <table style={{ width: '100%', fontSize: '14px' }}>
          <thead>
            <tr style={{ borderBottom: '2px solid #e5e7eb' }}>
              <th style={{ textAlign: 'left', padding: '0.5rem 0', fontWeight: 600 }}>Quarter</th>
              <th style={{ textAlign: 'right', padding: '0.5rem 0', fontWeight: 600 }}>Forecast</th>
              <th style={{ textAlign: 'right', padding: '0.5rem 0', fontWeight: 600 }}>Actual</th>
              <th style={{ textAlign: 'center', padding: '0.5rem 0', fontWeight: 600 }}>Tracker</th>
              <th style={{ textAlign: 'right', padding: '0.5rem 0', fontWeight: 600 }}>Bonus</th>
            </tr>
          </thead>
          <tbody>
            {data.quarterlySales.map((q, idx) => (
              <tr key={idx}>
                <td style={{ padding: '0.5rem 0' }}>{q.quarter}</td>
                <td style={{ textAlign: 'right' }}>{q.forecast != null ? currencyFormat(q.forecast) : '-'}</td>
                <td style={{ textAlign: 'right' }}>{currencyFormat(q.amount)}</td>
                <td style={{ textAlign: 'center' }}>
                  <span style={{
                    display: 'inline-block', padding: '2px 6px', borderRadius: '10px', fontSize: '12px', fontWeight: 600,
                    background: q.tracker >= 100 ? '#d1fae5' : q.tracker > 0 ? '#fee2e2' : '#f3f4f6',
                    color: q.tracker >= 100 ? '#065f46' : q.tracker > 0 ? '#991b1b' : '#9ca3af',
                  }}>
                    {q.tracker > 0 ? `${q.tracker.toFixed(0)}%` : '-'}
                  </span>
                </td>
                <td style={{ textAlign: 'right', color: q.bonusEarned ? '#059669' : '#6b7280' }}>
                  {q.bonusEarned ? currencyFormat(q.bonusAmount || 5000) : 'R 0.00'}
                </td>
              </tr>
            ))}
            <tr style={{ borderTop: '2px solid #e5e7eb', fontWeight: 600 }}>
              <td style={{ padding: '0.5rem 0' }}>Total YTD</td>
              <td style={{ textAlign: 'right' }}>
                {data.quarterlySales[0]?.forecast != null
                  ? currencyFormat(data.quarterlySales.reduce((sum, q) => sum + (q.forecast || 0), 0))
                  : '-'}
              </td>
              <td style={{ textAlign: 'right' }}>
                {currencyFormat(data.quarterlySales.reduce((sum, q) => sum + q.amount, 0))}
              </td>
              <td></td>
              <td style={{ textAlign: 'right', color: '#059669' }}>
                {currencyFormat(data.potentialIncentives.quarterlyBonus)}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default DashboardPage;
